import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../shared/api/errors';
import { Page, PageHeader } from '../../shared/ui/layout';
import { ListFooter } from '../../shared/ui/list-footer';
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NoMatchState,
} from '../../shared/ui/states';
import {
  flowFiltersToQuery,
  flowKeys,
  flowsAcrossFolders,
  listFlows,
  useFlowsLiveUpdate,
  type IFlowFilters,
  type TFlow,
} from '../../entities/flow';
import { folderKeys, isFolderGone, listFolders, moveItems } from '../../entities/folder';
import { listSources } from '../../entities/source';
import { listDestinations } from '../../entities/destination';
import { FolderCreate } from '../../features/folder-create';
import { FlowCreate } from '../../widgets/flow-create';
import { FolderBrowser } from '../../widgets/folder-browser';
import { flowColumns } from './ui/columns';
import { FlowFiltersBar } from './ui/filters-bar';
import { OrgEmptyState } from './ui/org-empty-state';
import { ProblemsEmptyState } from './ui/problems-empty-state';

interface IFlowsPageProps {
  filters: IFlowFilters;
  onFilters: (next: IFlowFilters) => void;
  onOpen: (flow: TFlow) => void;
  timeZone?: string;
}

const noFiltersActive = (filters: IFlowFilters): boolean =>
  !filters.startsFrom && !filters.state && !filters.hasProblems && !filters.q;

export const FlowsPage = ({ filters, onFilters, onOpen, timeZone }: IFlowsPageProps) => {
  const query = flowFiltersToQuery(filters);
  const flows = useQuery({ queryKey: flowKeys.list(query), queryFn: () => listFlows(query) });
  const folders = useQuery({
    queryKey: folderKeys.list('flows'),
    queryFn: () => listFolders('flows'),
  });
  useFlowsLiveUpdate();

  const across = flowsAcrossFolders(filters);
  const folderId = across ? null : (filters.folder ?? null);
  const folder = folderId
    ? (folders.data?.data.find((entry) => entry.id === folderId) ?? null)
    : null;
  // Opening a folder leaves a search: a filtered list is flat across every folder.
  const openFolder = (id: string | null) =>
    onFilters({ sort: filters.sort, folder: id ?? undefined });

  // A flow made while a folder is open is filed there before its editor opens.
  const created = (flow: TFlow) => {
    if (!folder) return onOpen(flow);
    void moveItems('flows', [flow.id], folder.id).finally(() => onOpen(flow));
  };

  const showOrgEmptyCheck =
    flows.isSuccess &&
    flows.data.total === 0 &&
    noFiltersActive(filters) &&
    !filters.folder &&
    folders.isSuccess &&
    folders.data.data.length === 0;
  // Always read: the "Starts from" menu lists every source, not only an empty org's count.
  const sources = useQuery({ queryKey: ['sources'], queryFn: listSources });
  const destinations = useQuery({
    queryKey: ['destinations'],
    queryFn: listDestinations,
    enabled: showOrgEmptyCheck,
  });

  const sourceOptionsForFilter = useMemo(
    () => (sources.data?.data ?? []).map((source) => ({ value: source.id, label: source.name })),
    [sources.data],
  );

  // "Today" is decided per fetch, not per render: a list left open past midnight re-reads it.
  const columns = useMemo(
    () => flowColumns(flows.dataUpdatedAt || Date.now(), timeZone, across),
    [flows.dataUpdatedAt, timeZone, across],
  );

  const rows = flows.data?.data ?? [];
  const isForbidden =
    flows.isError && flows.error instanceof ApiError && flows.error.status === 403;
  // "Nothing is failing" only when the problems filter is the one thing asked; with a search or
  // another filter on, an empty list means nothing matched, which is a different sentence.
  const onlyProblems = filters.hasProblems && !filters.q && !filters.startsFrom && !filters.state;
  const showProblemsEmpty = onlyProblems && flows.isSuccess && flows.data.total === 0;
  const showOrgEmpty =
    showOrgEmptyCheck &&
    (sources.isSuccess || sources.isError) &&
    (destinations.isSuccess || destinations.isError);
  const showNoMatch =
    flows.isSuccess && flows.data.total === 0 && !onlyProblems && !noFiltersActive(filters);

  const empty = showProblemsEmpty ? (
    <ProblemsEmptyState
      onShowAll={() => onFilters({ ...filters, hasProblems: undefined, cursor: undefined })}
    />
  ) : showNoMatch ? (
    <NoMatchState onReset={() => onFilters({ sort: filters.sort })} />
  ) : folderId ? undefined : (
    <EmptyState title="No flows yet" description="Nothing here yet." />
  );

  return (
    <>
      <PageHeader
        title="Flows"
        description="What happens to every request, from the moment it arrives."
        actions={
          <>
            {!showOrgEmpty && <FolderCreate scope="flows" parent={folder} />}
            <FlowCreate onCreated={created} folderId={folderId} />
          </>
        }
      />

      {!showOrgEmpty && (
        <FlowFiltersBar
          filters={filters}
          onFilters={onFilters}
          sourceOptions={sourceOptionsForFilter}
          hasProblemsCount={flows.data?.hasProblemsCount}
        />
      )}

      <Page width="full" className="flex-1">
        {showOrgEmpty ? (
          <OrgEmptyState
            sourceCount={sources.data?.data.length ?? 0}
            destinationCount={destinations.data?.data.length ?? 0}
            onCreated={onOpen}
          />
        ) : (
          <FolderBrowser
            scope="flows"
            noun="flow"
            folderId={folderId}
            onFolder={openFolder}
            searching={across}
            rows={rows}
            ready={flows.isSuccess}
            columns={columns}
            rowKey={(row) => row.id}
            rowLabel={(row) => row.name}
            rowPath={(row) => row.folderPath}
            onRowClick={onOpen}
            tall
            layoutId="flows"
            empty={empty}
            footer={
              rows.length > 0 && (
                <ListFooter
                  note="Last 24 hours. Runs = done + stopped + failed + in progress. Stopped means a Filter or Dedupe ended the run on purpose."
                  shown={rows.length}
                  total={flows.data?.total}
                  onNext={
                    flows.data?.nextCursor
                      ? () => onFilters({ ...filters, cursor: flows.data?.nextCursor ?? undefined })
                      : undefined
                  }
                />
              )
            }
          >
            {flows.isPending && <LoadingState rows={8} />}
            {flows.isError && isForbidden && <ForbiddenState />}
            {flows.isError && !isForbidden && !isFolderGone(flows.error) && (
              <ErrorState error={flows.error} onRetry={() => void flows.refetch()} />
            )}
          </FolderBrowser>
        )}
      </Page>
    </>
  );
};
